import React, { useContext, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { StyleContext } from 'utils/context/StyleContextProvider';
import ConditionalWrapper from 'utils/ConditionalWrapper';
import { CarrotArrowIcon as CarrotArrow } from '@viome/vnxt-nucleus';
import styles from './Dropdown.module.scss';

export default function Dropdown({
    name = 'Select',
    items,
    path,
    active,
    selectedItems,
    callback,
}) {
    const [isActive, setIsActive] = useState(false),
        onClick = () => {
            setIsActive(!isActive);
        },
        resetDropdown = (selected) => {
            setIsActive(false);
            if (callback && typeof callback === 'function') {
                callback(selected);
            }
        },
        parentStyles = useContext(StyleContext);

    let availableItems;

    if (selectedItems) {
        availableItems = items
            .filter((item) => !selectedItems.includes(item.sku))
            .map((item) => item.sku);
    }

    return (
        <div
            className={classNames(
                [styles.dropdown, parentStyles?.dropdown],
                isActive && [styles.active, parentStyles?.active],
                'absolute',
                'object-top',
                'z-10',
                'w-full',
            )}
            aria-label={`${name} select menu`}
            title={`${name} select menu`}
        >
            <div
                className={classNames(
                    [styles.cta, parentStyles?.cta],
                    'flex',
                    'items-center',
                    'justify-end',
                    'cursor-pointer',
                    'py-5',
                    'lg:pt-0',
                )}
                onClick={onClick}
                onKeyPress={onClick}
                role="button"
                tabIndex={0}
                aria-controls={`${name}_menu`}
                aria-expanded={isActive}
                aria-haspopup="menu"
            >
                <p className="heading-4 mr-5" role="presentation">
                    {active && active !== 'Most Recent' ? active : name}
                </p>
                <CarrotArrow />
            </div>
            <div
                className={classNames(
                    [styles.menu, parentStyles?.menu],
                    'overflow-hidden',
                )}
                id={`${name}_menu`}
            >
                <ul className="p-10" role="menu">
                    {items &&
                        items.map((item) => {
                            const itemTitle = item.title,
                                ddOptions = {};

                            let enabled = true;
                            if (availableItems) {
                                if (!availableItems.includes(item.sku)) {
                                    enabled = false;
                                }
                            }

                            if (enabled) {
                                ddOptions.onClick = () =>
                                    resetDropdown(itemTitle);

                                ddOptions.onKeyPress = () =>
                                    resetDropdown(itemTitle);

                                ddOptions.role = 'menuitem';
                                ddOptions.tabIndex = 0;
                            }

                            return (
                                <li
                                    key={`${name}_${itemTitle}`}
                                    role="presentation"
                                    className={classNames(
                                        itemTitle === active && [
                                            styles.active,
                                            parentStyles?.active,
                                        ],
                                        !enabled && [
                                            styles.disabled,
                                            parentStyles.disabled,
                                        ],
                                    )}
                                >
                                    <ConditionalWrapper
                                        condition={item.slug}
                                        wrapper={(nodeChildData) => (
                                            <Link href={`${path}${item.slug}`} legacyBehavior>
                                                {nodeChildData}
                                            </Link>
                                        )}
                                        elseWrapper={(nodeChildData) => (
                                            <>{nodeChildData}</>
                                        )}
                                    >
                                        <a {...ddOptions}>{itemTitle}</a>
                                    </ConditionalWrapper>
                                </li>
                            );
                        })}
                </ul>
            </div>
        </div>
    );
}
